import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { FaUndo } from 'react-icons/fa';
import Toggle from 'react-toggle';
import moment from 'moment';
import momentPropTypes from 'react-moment-proptypes';
import intlShape from '@/shared/utils/intlShape';

import PeriodButtonGroup from '@/shared/components/PeriodButtonGroup';
import UserSearch from '@/shared/components/input/UserSearch';
import AuthService from '@/shared/services/AuthService';
import DelayedDateRangePicker from '@/shared/components/input/DelayedDateRangePicker';
import { hasMultipleCompanies } from '@/shared/components/security/HasMultiCompany';
import HasMultipleLegalEntities from '@/shared/components/security/HasMultipleLegalEntities';
import CompanyMultiSelect from '@/shared/components/input/multiselect/CompanyMultiSelect';
import LegalEntityMultiSelect from '@/shared/components/input/multiselect/LegalEntityMultiSelect';
import EnumSelect from '@/shared/components/input/EnumSelect';
import EmployeeTagMultiSelect from '@/modules/hrm/EmployeeTagMultiSelect';
import messages from '../reportingMessages';

export const defaultFilters = {
  dateFrom: moment.utc().startOf('month'),
  dateUntil: moment.utc().endOf('month'),
  period: 'month',
  userId: null,
  ownCompanies: [],
  legalEntities: [],
  inDays: false,
  employeeType: null,
  onlyWithMissingHours: false,
  withEmployeeLabels: [],
};

class AbsencePerEmployeeFilters extends Component {
  state = {
    showUsers: false,
  };
  static propTypes = {
    onFilterChange: PropTypes.func.isRequired,
    filters: PropTypes.shape({
      userId: PropTypes.string,
      dateFrom: momentPropTypes.momentObj,
      dateUntil: momentPropTypes.momentObj,
      ownCompanies: PropTypes.arrayOf(PropTypes.string),
      inDays: PropTypes.bool,
      employeeType: PropTypes.number,
      onlyWithMissingHours: PropTypes.bool,
      withEmployeeLabels: PropTypes.arrayOf(PropTypes.string),
    }),
    intl: intlShape.isRequired,
  };
  static defaultProps = {
    filters: defaultFilters,
  };
  constructor(props) {
    super(props);
    this.state.showUsers = AuthService.hasPermission({
      functionality: 'RegistrationOthers',
      privilege: 'Read',
    });
  }

  filterChanged = filter => {
    const { filters, onFilterChange } = this.props;
    const newFilters = { ...filters, ...filter };

    if (JSON.stringify(filters) !== JSON.stringify(newFilters)) onFilterChange(newFilters);
  };
  resetFilters = () => {
    this.filterChanged({ ...defaultFilters });
  };
  dateChanged = (dateFrom, dateUntil) => {
    this.filterChanged({ dateFrom, dateUntil });
  };
  userChanged = item => {
    this.filterChanged({
      userId: (item && item.id) || null,
    });
  };

  render() {
    const { filters, intl } = this.props;
    const { showUsers } = this.state;
    const hasFilters = JSON.stringify(filters) !== JSON.stringify(defaultFilters);
    const showCompanies = hasMultipleCompanies();

    return (
      <div>
        <div className="row">
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <DelayedDateRangePicker
                startDate={filters.dateFrom}
                startDateId="AbsencePerEmployeeFiltersDateFrom"
                endDate={filters.dateUntil}
                endDateId="AbsencePerEmployeeFiltersDateUntil"
                onDatesChange={({ startDate, endDate }) => this.dateChanged(startDate, endDate)}
                block
              />
            </div>
          </div>
          <div className="col-lg-9">
            <div className="form-group">
              <PeriodButtonGroup
                dateFrom={filters.dateFrom}
                dateUntil={filters.dateUntil}
                period={filters.period}
                onChange={this.filterChanged}
              />
            </div>
          </div>
        </div>
        <div className="row">
          {showUsers && (
            <>
              <div className="col-lg-3 col-md-6">
                <div className="form-group">
                  <UserSearch
                    onChange={this.userChanged}
                    value={filters.userId}
                    placeholder={intl.formatMessage(messages.AllUsers)}
                  />
                </div>
              </div>
              <div className="col-lg-3 col-md-6">
                <div className="form-group">
                  <EnumSelect
                    enumType="employeeType"
                    value={filters.employeeType}
                    allowNull
                    placeholder={intl.formatMessage(messages.EmployeeType)}
                    onChange={value => this.filterChanged({ employeeType: value })}
                  />
                </div>
              </div>
              <div className="col-lg-3 col-md-6">
                <div className="form-group">
                  <EmployeeTagMultiSelect
                    value={filters.withEmployeeLabels}
                    onChange={withEmployeeLabels => this.filterChanged({ withEmployeeLabels })}
                    placeholder={intl.formatMessage(messages.WithEmployeeTags)}
                  />
                </div>
              </div>
            </>
          )}

          {showCompanies ? (
            <div className="col-lg-3 col-md-6">
              <div className="form-group">
                <CompanyMultiSelect
                  selection={filters.ownCompanies}
                  onChange={companies => this.filterChanged({ ownCompanies: companies })}
                />
              </div>
            </div>
          ) : null}
          <HasMultipleLegalEntities>
            <div className="col-lg-3 col-md-6">
              <div className="form-group">
                <LegalEntityMultiSelect
                  selection={filters.legalEntities || []}
                  onChange={legalEntities => this.filterChanged({ legalEntities })}
                />
              </div>
            </div>
          </HasMultipleLegalEntities>
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <label className="react-toggle-wrapper margin5T">
                <Toggle
                  checked={filters.inDays}
                  onChange={e => this.filterChanged({ inDays: e.target.checked })}
                />
                <FormattedMessage className="label-text" {...messages.ShowAbsencesInDays} />
              </label>
            </div>
          </div>
          {showUsers && (
            <div className="col-lg-3 col-md-6">
              <div className="form-group">
                <label className="react-toggle-wrapper margin5T">
                  <Toggle
                    checked={filters.onlyWithMissingHours}
                    onChange={e => this.filterChanged({ onlyWithMissingHours: e.target.checked })}
                  />
                  <FormattedMessage
                    className="label-text"
                    {...messages.OnlyEmployeesWithMissingHours}
                  />
                </label>
              </div>
            </div>
          )}
        </div>
        <div className="row">
          {hasFilters && (
            <div className="col-lg-3 col-md-6">
              <button
                type="button"
                className="btn btn-default btn-reset"
                onClick={this.resetFilters}
              >
                <FaUndo className="react-icon" />
                &nbsp;
                <FormattedMessage {...messages.ResetFilters} />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default injectIntl(AbsencePerEmployeeFilters);
